import {
  FluidIconConfig,
  FluidSize,
  FluidTheme,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { FunctionalComponent, h } from '@stencil/core';

import { FluidFunctionalComponentProps } from '../../../model/functional/fluid-functional-component-props.interface';
import { FluidButton } from '../fluid-button';

type ButtonIconPosition = 'before' | 'after' | 'self';

interface BtnPopoverProps extends FluidFunctionalComponentProps {
  host: FluidButton;
  identifier?: string;
}

// ================================================================ //
// -- Public Functional Components

/**
 * If Popover Props exist in Button instance, renders Popover as
 * "wrapper" for FLUID Button. If Popover Props do NOT exist, only
 * renders FLUID Button instance WITHOUT Popover.
 */
export const ButtonPopover: FunctionalComponent<BtnPopoverProps> = (
  props: BtnPopoverProps,
  children
) => {
  const { host, identifier } = props;

  if (!host.hasPopover()) {
    return children;
  } else {
    const config = host.getPopoverConfig();
    return (
      <fluid-popover
        data-test-id={`${identifier}-popover`}
        {...config}
        theme={host.theme}
        overrideGlobalTheme={true}
        asWrapper={true}>
        {children}
      </fluid-popover>
    );
  }
};

/**
 * Renders Button's label IFF one is provided AND the Button isn't
 * an "icon-only" Button.
 */
export const ButtonLabel = (props: {
  label: string;
  iconButton: boolean;
  identifier: string;
}) => {
  const { label, iconButton, identifier } = props;

  return (
    !!label &&
    !iconButton && (
      <span class='btn-label' data-test-id={`${identifier}-btn-label`}>
        {label}
      </span>
    )
  );
};

/**
 * Determines if an Icon exists (either native or slotted), &
 * renders the appropriate Icon or icon slot based on position.
 */
export const ButtonIcon = (props: {
  config: FluidIconConfig;
  host: HTMLFluidButtonElement;
  position: ButtonIconPosition;
  identifier?: string;
}) => {
  const { config, host, position, identifier } = props;

  return config ? (
    <NativeIcon
      position={position}
      config={config}
      theme={host.theme}
      identifier={identifier}
    />
  ) : (
    <IconSlot
      host={host}
      position={position}
      data-test-id={`${identifier}-slot-${position}`}
    />
  );
};

/**
 * Renders a native FLUID Icon for the specified position.
 */
export const NativeIcon = (props: {
  position: ButtonIconPosition;
  config?: FluidIconConfig;
  theme?: FluidTheme;
  identifier?: string;
}) => {
  const { position, config, theme, identifier } = props;
  const size = getIconSize(config, position);

  return (
    <fluid-icon
      class={`icon-${position} icon-${size}`}
      config={{
        ...config,
        size,
        theme,
        overrideGlobalTheme: true,
        inline: true,
        identifier: { identifier },
      }}
      data-test-id={`${identifier}-icon-${position}`}
    />
  );
};

/**
 * Renders a slot for a slotted Button icon.
 */
export const IconSlot = (props: {
  position: ButtonIconPosition;
  host: HTMLFluidButtonElement;
  identifier?: string;
}) => {
  const { position, host, identifier } = props;
  if (host.querySelector(`[slot='icon-${position}']`)) {
    return (
      <span
        class={`icon-container icon-${position}`}
        data-test-id={`${identifier}-icon-container`}>
        <slot
          name={`icon-${position}`}
          data-test-id={`${identifier}-icon-${position}-slot`}
        />
      </span>
    );
  }
};

// ================================================================ //
// -- Functional Component Utilities (Private)

const getIconSize = (
  config: FluidIconConfig,
  position: ButtonIconPosition
): FluidSize => {
  if (isNullOrUndefined(config)) return;

  const defaultSize =
    position === 'self' ? FluidSize.SMALL : FluidSize.EXTRA_SMALL;

  switch (config.size) {
    case undefined:
    case null:
      return defaultSize;
    case FluidSize.EXTRA_LARGE:
    case FluidSize.FULL:
      return FluidSize.LARGE;
    default:
      return config.size;
  }
};
